import React, { useEffect, useState } from 'react';
import { updateEgressSettings } from '../../../services/egress.service';
import { toUserFacingErrorMessage } from '../../../services/_core';

const money = (usage, value) => `${usage.currency || 'AUD'} ${Number(value || 0).toFixed(2)}`;
/** Data used: MB below 1 GB, so small amounts don't read as 0 GB (1 GB = 1,000 MB, as billed). */
const amount = (usage) => {
  const bytes = Number(usage?.bytes || 0);
  if (bytes < 1e9) return `${(bytes / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
  return `${(bytes / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
};
const day = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** One line for the scan modal: what rotating residential has cost this month. */
export function UsageLine({ usage }) {
  if (!usage) return null;
  const limit = usage.limit != null ? ` of ${money(usage, usage.limit)} limit` : '';
  return (
    <p className={`settings-modal-card-hint${usage.limit_reached ? ' scan-network-usage-warn' : ''}`}>
      This month: {amount(usage)} · {money(usage, usage.cost)}{limit}
      {usage.limit_reached ? (usage.limit_action === 'STANDARD' ? ' · limit reached, scans use standard scanning' : ' · limit reached, rotating scans stop') : ''}
    </p>
  );
}

/** Settings → Scan network: month-to-date meter, and the spending limit (organization admins). */
export default function RotatingUsageMeter({ orgId, usage, limitActions = [], onSaved }) {
  const [limit, setLimit] = useState(usage?.limit ?? '');
  const [action, setAction] = useState(usage?.limit_action || 'STOP');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setLimit(usage?.limit ?? '');
    setAction(usage?.limit_action || 'STOP');
  }, [usage?.limit, usage?.limit_action]);

  if (!usage) return null;
  const percent = usage.percent_of_limit;
  const dirty = String(limit ?? '') !== String(usage.limit ?? '') || action !== (usage.limit_action || 'STOP');

  const save = async () => {
    const value = String(limit).trim();
    if (value !== '' && !(Number(value) >= 0)) {
      setError('Enter the limit as an amount, for example 50, or leave it blank for no limit.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await updateEgressSettings(orgId, {
        usage_limit_aud: value === '' ? null : Number(value).toFixed(2),
        usage_limit_action: action,
      });
      setMessage('Limit saved.');
      await onSaved?.();
    } catch (err) {
      setError(toUserFacingErrorMessage(err?.message, 'Could not save the limit.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="scan-network-usage">
      <div className="scan-network-usage-head">
        <div>
          <span className="scan-network-usage-figure">{amount(usage)}</span>
          <span className="scan-network-usage-sub">
            {money(usage, usage.cost)} this month · {money(usage, usage.price_per_gb)} per GB · resets {day(usage.period_end)}
          </span>
        </div>
        {usage.limit != null ? (
          <span className={`scan-network-usage-limit${usage.limit_reached ? ' is-reached' : ''}`}>
            {usage.limit_reached ? 'Limit reached' : `${percent ?? 0}% of ${money(usage, usage.limit)}`}
          </span>
        ) : (
          <span className="scan-network-usage-limit">No limit</span>
        )}
      </div>
      {usage.limit != null ? (
        <div className="scan-network-usage-bar" role="progressbar" aria-label="Rotating residential spend this month"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent ?? 0}>
          <span style={{ width: `${Math.min(percent ?? 0, 100)}%` }} className={percent >= 80 ? 'is-high' : ''} />
        </div>
      ) : null}

      <div className="scan-network-proxy-row">
        <label className="scan-network-field" htmlFor="scan-network-usage-limit">
          <span>Monthly limit ({usage.currency || 'AUD'})</span>
          <input id="scan-network-usage-limit" className="scan-network-input" inputMode="decimal" placeholder="No limit"
            value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9.]/g, ''))} disabled={saving} />
        </label>
        <label className="scan-network-field scan-network-field--grow" htmlFor="scan-network-usage-action">
          <span>When the limit is reached</span>
          <select id="scan-network-usage-action" className="scan-network-select" value={action}
            onChange={(e) => setAction(e.target.value)} disabled={saving}>
            {limitActions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </label>
      </div>
      <p className="deployment-section-desc">
        Admins are emailed at 80% and 100% of the limit. Usage is billed on your subscription invoice.
      </p>
      {error ? <div className="scan-network-alert scan-network-alert--error" role="alert">{error}</div> : null}
      {message ? <div className="scan-network-alert" role="status">{message}</div> : null}
      <div className="scan-network-actions">
        <button type="button" className="scan-network-save" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save limit'}
        </button>
      </div>
    </div>
  );
}
